import { createFileRoute } from "@tanstack/react-router";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { GamePageView } from "@/components/GamePageView";
import { gameOgImage } from "@/lib/og-images";
import { buildGameFaqs, gameMetaDescription, gameTitle, type GameCopyInput } from "@/lib/page-copy";
import { relatedGamesForGame, relatedStatesForGame } from "@/lib/related-links";
import {
  articleSchema,
  breadcrumbSchema,
  faqPageSchema,
  gameSchema,
  itemListSchema,
} from "@/lib/jsonld";

export const Route = createFileRoute("/$stateSlug/$gameSlug/")({
  head: ({ params }) => {
    const game = humanizeSlug(params.gameSlug);
    const state = humanizeSlug(params.stateSlug);
    const path = `/${params.stateSlug}/${params.gameSlug}`;
    const copy: GameCopyInput = {
      name: game,
      slug: params.gameSlug,
      stateName: state,
      stateSlug: params.stateSlug,
    };
    const title = gameTitle(copy);
    const description = gameMetaDescription(copy);
    const image = gameOgImage(params.gameSlug, params.stateSlug);
    const faqs = buildGameFaqs(copy);
    const relatedGames = relatedGamesForGame(`${params.stateSlug}-${params.gameSlug}`, params.stateSlug);
    const relatedStateLinks = relatedStatesForGame(params.gameSlug, false, params.stateSlug);
    return buildSeo({
      title,
      description,
      path,
      type: "article",
      image,
      jsonLd: [
        gameSchema({
          name: `${game} (${state})`,
          path,
          description: `${state} ${game} draw game: latest winning numbers, jackpot, draw schedule and results archive.`,
          stateName: state,
        }),
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name: "States", item: "/states" },
          { name: state, item: `/states/${params.stateSlug}` },
          { name: game, item: path },
        ]),
        faqPageSchema(faqs),
        articleSchema({
          headline: `${state} ${game} Results & Winning Numbers`,
          description,
          path,
          image,
          section: "Lottery results",
          keywords: [`${state} ${game}`, `${game} results`, `${state} lottery`, `${game} winning numbers`],
        }),
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `${state} ${game} winning numbers`,
          description: `Historical and latest ${game} winning numbers drawn for ${state} players.`,
          url: path,
          keywords: [`${game} results`, `${state} lottery`, `${game} winning numbers`, `${state} ${game}`],
        },
        itemListSchema(`Other ${state} lottery games`, relatedGames),
        itemListSchema("Related state lottery results", relatedStateLinks),
      ],
    });
  },
  component: StateGameRoute,
});

function StateGameRoute() {
  const { stateSlug, gameSlug } = Route.useParams();
  return <GamePageView gameSlug={gameSlug} stateSlug={stateSlug} />;
}